import { create } from 'zustand';
import type { ScheduledEvent, ScheduledEventInterestUpdate, ScheduledEventPayload } from '@spawnwire/shared';
import { http } from '../../lib/api';
import { mergeEvent, removeEvent, replaceEvent, sortEvents, withInterestCount } from './logic';

/**
 * Each space's events that are still to come or running, for the Events screen and the sidebar's count. A space is
 * loaded the first time it is looked at; after that the server's live events keep it current. Spaces that were
 * never loaded ignore live events, since loading later gets everything.
 */
export const useEvents = create<{ bySpace: Record<string, ScheduledEvent[] | undefined> }>(() => ({ bySpace: {} }));

const put = (spaceId: string, list: ScheduledEvent[]) => useEvents.setState((s) => ({ bySpace: { ...s.bySpace, [spaceId]: list } }));

/** Counts requests per space, so an old answer that arrives late never overwrites a newer one. */
const requested = new Map<string, number>();

export const events = {
  async load(spaceId: string): Promise<void> {
    const n = (requested.get(spaceId) ?? 0) + 1;
    requested.set(spaceId, n);
    const res = await http.get<{ data: ScheduledEvent[] }>(`/spaces/${spaceId}/events?status=live&limit=100`);
    if (requested.get(spaceId) === n) put(spaceId, sortEvents(res.data));
  },
  /** Loads every space that was loaded before: after the connection came back, when events may have been missed. */
  async reloadLoaded(): Promise<void> {
    await Promise.all(Object.keys(useEvents.getState().bySpace).map((id) => events.load(id).catch(() => {})));
  },
  /** An event was created or changed. */
  apply(event: ScheduledEventPayload): void {
    const list = useEvents.getState().bySpace[event.spaceId];
    if (list) put(event.spaceId, mergeEvent(list, event));
  },
  remove(spaceId: string, id: string): void {
    const list = useEvents.getState().bySpace[spaceId];
    if (list) put(spaceId, removeEvent(list, id));
  },
  applyInterest(update: ScheduledEventInterestUpdate): void {
    const list = useEvents.getState().bySpace[update.spaceId];
    if (list) put(update.spaceId, withInterestCount(list, update.eventId, update.interestedCount));
  },
  /** The event as the server answered this person's own request. */
  set(event: ScheduledEvent): void {
    const list = useEvents.getState().bySpace[event.spaceId];
    if (list) put(event.spaceId, replaceEvent(list, event));
  },
  /** Forgets a space's events, when it is left or deleted. */
  forget(spaceId: string): void {
    useEvents.setState((s) => {
      const { [spaceId]: _gone, ...rest } = s.bySpace;
      return { bySpace: rest };
    });
  },
};
